/**
 * Coach-voice play button (Phase B, output slice).
 *
 * Fetches the synthesized audio for a coach message and plays it. Self-contained
 * loading/playing state. Rendered only when voice is enabled (`VOICE_ENABLED`) and
 * the platform supports playback. Fails closed silently — voice output never
 * blocks reading the text reply.
 */
import Ionicons from "@expo/vector-icons/Ionicons";
import { useCallback, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import i18n from "@/i18n";
import { useAppTheme } from "@/theme/app-theme";
import type { PracticeVoice } from "@/types";

import { COACH_AUDIO_SUPPORTED, playCoachMessage, stopCoachAudio } from "./coach-audio";
import {
  markPlaybackEnd,
  markPlaybackStart,
  setPlaybackDuration,
} from "./coach-playback";
import { VOICE_ENABLED } from "./flag";
import { markManualReplayEnd, markManualReplayStart } from "./manual-replay";

export function CoachAudioButton({
  messageId,
  voice,
}: {
  messageId: string;
  voice: PracticeVoice;
}) {
  const { colors } = useAppTheme();
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  const onPress = useCallback(async () => {
    if (playing) {
      stopCoachAudio();
      setPlaying(false);
      markManualReplayEnd(); // let the mic resume immediately on manual stop
      return;
    }
    setBusy(true);
    setFailed(false);
    // Pause the voice-mode mic for the whole replay so it doesn't capture and
    // auto-send the coach's own voice; the mic resumes on end/fail below.
    markManualReplayStart();
    try {
      await playCoachMessage(messageId, voice, {
        onStart: () => {
          setBusy(false);
          setPlaying(true);
          markPlaybackStart(messageId); // tracked for barge-in
        },
        onDuration: (ms) => setPlaybackDuration(messageId, ms),
      });
    } catch {
      setFailed(true); // e.g. TTS disabled (503) — fail closed to text.
    } finally {
      markPlaybackEnd(messageId);
      markManualReplayEnd();
      setBusy(false);
      setPlaying(false);
    }
  }, [messageId, playing, voice]);

  if (!VOICE_ENABLED || !COACH_AUDIO_SUPPORTED) return null;

  return (
    <View style={styles.row}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={i18n.t(
          playing ? "practice.ai.stopVoiceA11y" : "practice.ai.playVoiceA11y",
        )}
        style={styles.btn}
        hitSlop={8}
      >
        {busy ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : (
          <Ionicons
            name={playing ? "stop-circle-outline" : "volume-high-outline"}
            size={18}
            color={failed ? colors.textMuted : colors.primary}
          />
        )}
        <Text style={[styles.label, { color: failed ? colors.textMuted : colors.primary }]}>
          {i18n.t(
            failed
              ? "practice.ai.voiceUnavailable"
              : playing
                ? "practice.ai.stop"
                : "practice.ai.playVoice",
          )}
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { marginTop: 4 },
  btn: { flexDirection: "row", alignItems: "center", gap: 5, minHeight: 28 },
  label: { fontSize: 12, fontWeight: "600" },
});
